import React, { useState, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';

interface StellarSystemProps {
  phase: 'idle' | 'expanding' | 'giant';
  onExpansionComplete: () => void;
}

function StellarSystem({ phase, onExpansionComplete }: StellarSystemProps) {
  const envelopeRef = useRef<THREE.Mesh>(null);
  const coreRef = useRef<THREE.Mesh>(null);
  const pointLightRef = useRef<THREE.PointLight>(null);

  // Orbit angle state
  const mercuryAngleRef = useRef(0);
  const venusAngleRef = useRef(Math.PI * 0.6);
  const earthAngleRef = useRef(Math.PI * 1.3);

  // Refs for positions
  const [mercuryPos, setMercuryPos] = useState<[number, number, number]>([1.2, 0, 0]);
  const [venusPos, setVenusPos] = useState<[number, number, number]>([1.8, 0, 0]);
  const [earthPos, setEarthPos] = useState<[number, number, number]>([2.5, 0, 0]);

  // Visibility state
  const [showMercury, setShowMercury] = useState(true);
  const [showVenus, setShowVenus] = useState(true);
  const [earthFlash, setEarthFlash] = useState(false);

  const phaseTimeRef = useRef(0);
  const totalTimeRef = useRef(0);

  useFrame((state, delta) => {
    totalTimeRef.current += delta;

    // Orbit calculations
    if (phase === 'idle') {
      mercuryAngleRef.current += delta * 1.5;
      venusAngleRef.current += delta * 0.8;
      earthAngleRef.current += delta * 0.45;
    } else {
      // Slower orbits during death phase
      mercuryAngleRef.current += delta * 0.4;
      venusAngleRef.current += delta * 0.2;
      earthAngleRef.current += delta * 0.15;
    }

    setMercuryPos([
      Math.cos(mercuryAngleRef.current) * 1.2,
      0,
      Math.sin(mercuryAngleRef.current) * 1.2
    ]);

    setVenusPos([
      Math.cos(venusAngleRef.current) * 1.8,
      0,
      Math.sin(venusAngleRef.current) * 1.8
    ]);

    setEarthPos([
      Math.cos(earthAngleRef.current) * 2.5,
      0,
      Math.sin(earthAngleRef.current) * 2.5
    ]);

    // Phase transitions and styling
    if (phase === 'idle') {
      phaseTimeRef.current = 0;
      setShowMercury(true);
      setShowVenus(true);
      setEarthFlash(false);

      // Reset camera position slowly
      state.camera.position.lerp(new THREE.Vector3(0, 4, 8), delta * 2.0);

      // Star sizing & materials
      if (envelopeRef.current) {
        envelopeRef.current.scale.setScalar(1);
        const mat = envelopeRef.current.material as THREE.MeshStandardMaterial;
        mat.color.set('#FFD700');
        mat.emissive.set('#FFD700');
        mat.emissiveIntensity = 2.0;
        mat.opacity = 0.85;
      }
      if (coreRef.current) {
        coreRef.current.scale.setScalar(1);
        const mat = coreRef.current.material as THREE.MeshStandardMaterial;
        mat.emissiveIntensity = 2.0;
      }
      if (pointLightRef.current) {
        pointLightRef.current.intensity = 3.0;
        pointLightRef.current.color.set('#FFD700');
      }
    } else if (phase === 'expanding') {
      phaseTimeRef.current += delta;
      const t = Math.min(1.0, phaseTimeRef.current / 3.0);

      // Shrink core, expand envelope
      const envScale = 1.0 + t * (7.0 - 1.0); // 0.5 * 7 = 3.5 radius
      const coreScale = 1.0 - t * 0.67; // 0.15 * 0.33 = 0.05 radius

      if (envelopeRef.current) {
        envelopeRef.current.scale.setScalar(envScale);
        const mat = envelopeRef.current.material as THREE.MeshStandardMaterial;

        // Color transition
        const col = new THREE.Color('#FFD700').lerp(new THREE.Color('#DC2626'), t);
        mat.color.copy(col);
        mat.emissive.copy(col);
        mat.emissiveIntensity = 2.0 - t * 0.5;
        mat.opacity = 0.85 - t * 0.25; // gets slightly more transparent
      }

      if (coreRef.current) {
        coreRef.current.scale.setScalar(coreScale);
        const mat = coreRef.current.material as THREE.MeshStandardMaterial;
        // Core flashes rapidly
        mat.emissiveIntensity = 2.0 + Math.abs(Math.sin(phaseTimeRef.current * 30)) * 6.0;
      }

      if (pointLightRef.current) {
        pointLightRef.current.intensity = 3.0 + t * 4.0;
        const col = new THREE.Color('#FFD700').lerp(new THREE.Color('#DC2626'), t);
        pointLightRef.current.color.copy(col);
      }

      // Swallow planets
      if (phaseTimeRef.current >= 0.8) setShowMercury(false);
      if (phaseTimeRef.current >= 1.8) setShowVenus(false);
      if (phaseTimeRef.current >= 2.5) setEarthFlash(true);

      if (phaseTimeRef.current >= 3.0) {
        onExpansionComplete();
      }
    } else if (phase === 'giant') {
      // Pulsing effect
      const pulse = Math.sin(totalTimeRef.current * 1.5) * 0.03;
      const baseScale = 7.0; // 3.5 radius / 0.5 initial radius
      
      if (envelopeRef.current) {
        envelopeRef.current.scale.setScalar(baseScale + pulse * baseScale);
        const mat = envelopeRef.current.material as THREE.MeshStandardMaterial;
        mat.color.set('#DC2626');
        mat.emissive.set('#DC2626');
        mat.emissiveIntensity = 1.5;
        mat.opacity = 0.65;
      }

      if (coreRef.current) {
        coreRef.current.scale.setScalar(0.33); // 0.05 radius
        const mat = coreRef.current.material as THREE.MeshStandardMaterial;
        mat.emissiveIntensity = 1.0;
      }

      if (pointLightRef.current) {
        pointLightRef.current.intensity = 6.0;
        pointLightRef.current.color.set('#DC2626');
      }

      // Camera zoom out
      state.camera.position.lerp(new THREE.Vector3(0, 7, 14), delta * 1.0);
    }
  });

  return (
    <group>
      {/* Central Star Core */}
      <mesh ref={coreRef}>
        <sphereGeometry args={[0.15, 32, 32]} />
        <meshStandardMaterial color="#FFFFFF" emissive="#FFFFFF" emissiveIntensity={2} />
      </mesh>

      {/* Central Star Envelope */}
      <mesh ref={envelopeRef}>
        <sphereGeometry args={[0.5, 64, 64]} />
        <meshStandardMaterial
          color="#FFD700"
          emissive="#FFD700"
          emissiveIntensity={2.0}
          transparent
          opacity={0.85}
          roughness={0.4}
        />
      </mesh>

      {/* Point Light inside the star */}
      <pointLight ref={pointLightRef} position={[0, 0, 0]} intensity={3.0} decay={1.5} distance={25} />

      {/* Orbit Rings */}
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <torusGeometry args={[1.2, 0.008, 8, 64]} />
        <meshBasicMaterial color={showMercury ? '#FFFFFF' : '#EF4444'} transparent opacity={showMercury ? 0.3 : 0.08} />
      </mesh>

      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <torusGeometry args={[1.8, 0.008, 8, 64]} />
        <meshBasicMaterial color={showVenus ? '#FFFFFF' : '#EF4444'} transparent opacity={showVenus ? 0.3 : 0.08} />
      </mesh>

      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <torusGeometry args={[2.5, 0.008, 8, 64]} />
        <meshBasicMaterial color={earthFlash ? (Math.sin(totalTimeRef.current * 15) > 0 ? '#EF4444' : '#FFA500') : '#FFFFFF'} transparent opacity={0.4} />
      </mesh>

      {/* Planets */}
      {showMercury && (
        <mesh position={mercuryPos}>
          <sphereGeometry args={[0.04, 16, 16]} />
          <meshStandardMaterial color="#8C7D70" roughness={0.8} />
        </mesh>
      )}

      {showVenus && (
        <mesh position={venusPos}>
          <sphereGeometry args={[0.07, 16, 16]} />
          <meshStandardMaterial color="#D5B086" roughness={0.7} />
        </mesh>
      )}

      <mesh position={earthPos}>
        <sphereGeometry args={[0.08, 16, 16]} />
        <meshStandardMaterial color={earthFlash ? '#FF5722' : '#3B82F6'} roughness={0.6} emissive={earthFlash ? '#FF5722' : '#000000'} emissiveIntensity={earthFlash ? 0.8 : 0} />
      </mesh>
    </group>
  );
}

export function RedGiantExpansion() {
  const [phase, setPhase] = useState<'idle' | 'expanding' | 'giant'>('idle');

  const handleTrigger = () => {
    if (phase === 'idle') {
      setPhase('expanding');
    }
  };

  const handleReset = () => {
    setPhase('idle');
  };

  let buttonText = '▶ Trigger Expansion';
  if (phase === 'expanding') buttonText = 'Expanding...';
  if (phase === 'giant') buttonText = 'Red Giant';

  return (
    <div className="relative w-full h-[340px] rounded-xl overflow-hidden bg-[#0F172A] border border-white/10 select-none">
      <Canvas camera={{ position: [0, 4, 8], fov: 45 } as any}>
        <color attach="background" args={['#0F172A']} />
        <fog attach="fog" args={['#0F172A', 10, 30]} />
        <ambientLight intensity={0.2} />
        <StellarSystem phase={phase} onExpansionComplete={() => setPhase('giant')} />
        <OrbitControls enableZoom={true} enablePan={false} autoRotate={phase === 'idle'} autoRotateSpeed={0.3} />
      </Canvas>

      <div className="absolute top-3 left-3 pointer-events-none select-none text-white/40 text-sm font-medium">
        Sun-Like Star: Red Giant Expansion
      </div>

      {/* Control overlay */}
      <div className="absolute bottom-4 inset-x-0 flex justify-center gap-4">
        <button
          onClick={handleTrigger}
          disabled={phase !== 'idle'}
          className={`px-4 py-2 rounded-lg font-bold text-xs uppercase tracking-wider transition-all duration-300 ${
            phase === 'idle'
              ? 'bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-500/20'
              : 'bg-white/10 text-white/50 cursor-not-allowed border border-white/5'
          }`}
        >
          {buttonText}
        </button>

        {phase === 'giant' && (
          <button
            onClick={handleReset}
            className="px-4 py-2 rounded-lg font-bold text-xs uppercase tracking-wider bg-white/20 hover:bg-white/30 text-white border border-white/10 transition-all duration-300"
          >
            ↺ Reset
          </button>
        )}
      </div>
    </div>
  );
}
